/**
 * WorkgroupDetail (Phase 2.A.1).
 *
 * Right-side drawer for one workgroup. Shows metadata + member list +
 * inline edit + add/remove member + CSV bulk import. Designed for scale
 * — member list pages server-side; search input filters live; new
 * members are added optimistically (TanStack Query invalidation).
 *
 * Three modes via props:
 *   - mode='detail' + wgId   → load + edit existing
 *   - mode='create'          → blank form, on save: create + switch to detail
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  AlertTriangle,
  Check,
  Edit2,
  ShieldCheck,
  Trash2,
  Upload,
  UserPlus,
  X,
} from 'lucide-react';

import {
  ApiError,
  parseMembershipCsv,
  useAddWgMember,
  useCreateWorkgroup,
  useDeleteWorkgroup,
  useImportWgMembersBulk,
  useRemoveWgMember,
  useUpdateWorkgroup,
  useWorkgroup,
  useWorkgroupMembers,
  useCatalogTools,
  useCatalogServers,
  useUpdateTool,
} from '@/api/index.js';
import { Drawer } from '../Drawer.js';
import { HelpCallout } from '../HelpCallout.js';
import { Required, RequiredLegend } from '../Required.js';

interface WorkgroupDetailProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: 'detail' | 'create';
  readonly wgId?: string;
  /** Called after successful create/update so the caller can refresh selection. */
  readonly onPersisted?: (id: string) => void;
}

export function WorkgroupDetail({
  open,
  onClose,
  mode,
  wgId,
  onPersisted,
}: WorkgroupDetailProps): JSX.Element {
  const wg = useWorkgroup(mode === 'detail' ? wgId : undefined);
  const members = useWorkgroupMembers(mode === 'detail' ? wgId : undefined);

  const create = useCreateWorkgroup();
  const update = useUpdateWorkgroup();
  const addMember = useAddWgMember();
  const removeMember = useRemoveWgMember();
  const bulkImport = useImportWgMembersBulk();
  const deleteWg = useDeleteWorkgroup();

  const [editing, setEditing] = useState(mode === 'create');
  const [formId, setFormId] = useState('');
  const [formDisplay, setFormDisplay] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formOwner, setFormOwner] = useState('');
  const [formReviewer, setFormReviewer] = useState(false);
  const [newMember, setNewMember] = useState('');
  const [csvText, setCsvText] = useState('');
  const [showCsv, setShowCsv] = useState(false);
  const [memberFilter, setMemberFilter] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  // Drawer tab. Create mode is metadata-only; detail mode has 3 tabs.
  const [tab, setTab] = useState<'metadata' | 'members' | 'access'>('metadata');
  useEffect(() => {
    // Reset to metadata when switching modes / opening a different WG.
    setTab('metadata');
  }, [mode, wgId]);
  const [csvResult, setCsvResult] = useState<{
    imported: number;
    errors: { row: unknown; reason: string }[];
  } | null>(null);

  // Sync form state when WG loads in detail mode.
  useEffect(() => {
    if (mode === 'detail' && wg.data) {
      setFormId(wg.data.id);
      setFormDisplay(wg.data.display_name);
      setFormDesc(wg.data.description ?? '');
      setFormOwner(wg.data.owner_sunet ?? '');
      setFormReviewer(wg.data.is_reviewer_wg);
      setEditing(false);
    } else if (mode === 'create' && open) {
      setFormId('');
      setFormDisplay('');
      setFormDesc('');
      setFormOwner('');
      setFormReviewer(false);
      setEditing(true);
    }
  }, [mode, wg.data, open]);

  const filteredMembers = useMemo(() => {
    const items = members.data?.items ?? [];
    const q = memberFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => m.principal_sub.toLowerCase().includes(q));
  }, [members.data?.items, memberFilter]);

  function handleResult<T>(p: Promise<T>, after?: (v: T) => void): void {
    setActionError(null);
    p.then((v) => {
      if (after) after(v);
    }).catch((err: unknown) => {
      setActionError(
        err instanceof ApiError
          ? `${err.status}: ${JSON.stringify(err.body)}`
          : (err as Error).message,
      );
    });
  }

  function onSave(): void {
    setActionError(null);
    if (mode === 'create') {
      if (!formId.match(/^mcp:[a-z0-9-]+$/)) {
        setActionError('id must match mcp:<slug> (a-z, 0-9, -)');
        return;
      }
      handleResult(
        create.mutateAsync({
          id: formId,
          display_name: formDisplay,
          description: formDesc || undefined,
          owner_sunet: formOwner || undefined,
          is_reviewer_wg: formReviewer,
        }),
        (v) => {
          onPersisted?.(v.id);
          onClose();
        },
      );
      return;
    }
    if (!wg.data) return;
    handleResult(
      update.mutateAsync({
        id: wg.data.id,
        patch: {
          display_name: formDisplay,
          description: formDesc || undefined,
          owner_sunet: formOwner || undefined,
          is_reviewer_wg: formReviewer,
        },
      }),
      (v) => {
        setEditing(false);
        onPersisted?.(v.id);
      },
    );
  }

  function onAddMember(): void {
    if (!wg.data || !newMember.trim()) return;
    handleResult(addMember.mutateAsync({ wgId: wg.data.id, principalSub: newMember.trim() }), () =>
      setNewMember(''),
    );
  }

  function onRemoveMember(sub: string): void {
    if (!wg.data) return;
    if (!window.confirm(`Remove ${sub} from ${wg.data.id}?`)) return;
    handleResult(removeMember.mutateAsync({ wgId: wg.data.id, principalSub: sub }));
  }

  function onCsvImport(): void {
    if (!wg.data) return;
    const rows = parseMembershipCsv(csvText, wg.data.id);
    if (rows.length === 0) {
      setActionError('CSV had no valid rows. Format: principal_sub[,expires_at] per line.');
      return;
    }
    handleResult(bulkImport.mutateAsync({ rows }), (r) => {
      setCsvResult({ imported: r.imported, errors: r.errors });
      // Keep the panel open if there were per-row errors so the admin
      // can read them; only auto-close on a fully clean import.
      if (r.errors.length === 0) {
        setCsvText('');
        setShowCsv(false);
      }
    });
  }

  const titleEl = mode === 'create' ? 'Create workgroup' : (wg.data?.display_name ?? wgId ?? '...');
  const persisting =
    create.isPending ||
    update.isPending ||
    addMember.isPending ||
    removeMember.isPending ||
    bulkImport.isPending;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Workgroup"
      title={titleEl}
      footer={
        editing ? (
          <>
            <button
              type="button"
              onClick={() => (mode === 'create' ? onClose() : setEditing(false))}
            >
              <X size={14} aria-hidden /> Cancel
            </button>
            <button
              type="button"
              className="primary"
              onClick={onSave}
              disabled={persisting || !formDisplay || (mode === 'create' && !formId)}
            >
              <Check size={14} aria-hidden /> {mode === 'create' ? 'Create' : 'Save'}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setEditing(true)}>
              <Edit2 size={14} aria-hidden /> Edit
            </button>
            {mode === 'detail' && wg.data && wg.data.id !== 'mcp:platform-admins' && (
              <button
                type="button"
                onClick={async () => {
                  if (!wg.data) return;
                  if (
                    !confirm(
                      `Delete workgroup "${wg.data.id}"? This cannot be undone.\n\n` +
                        `If any servers or tools still reference it, the request will be blocked with a list — fix those first.`,
                    )
                  )
                    return;
                  setActionError(null);
                  try {
                    await deleteWg.mutateAsync(wg.data.id);
                    onClose();
                  } catch (e) {
                    if (e instanceof ApiError && e.status === 409) {
                      const body = e.body as {
                        detail?: string;
                        blockers?: { type: string; id: string; field?: string }[];
                      };
                      const blockerLines = (body.blockers ?? [])
                        .map((b) => `  • ${b.type} ${b.id}${b.field ? ` (${b.field})` : ''}`)
                        .join('\n');
                      setActionError(
                        `${body.detail ?? 'WG is in use'}${
                          blockerLines ? `\n${blockerLines}` : ''
                        }`,
                      );
                    } else {
                      setActionError(
                        e instanceof ApiError
                          ? `${e.status}: ${JSON.stringify(e.body)}`
                          : (e as Error).message,
                      );
                    }
                  }
                }}
                style={{
                  border: '1px solid #d97766',
                  color: '#c0392b',
                  background: 'rgba(255,255,255,0.85)',
                }}
                title="Delete this workgroup"
              >
                <Trash2 size={14} aria-hidden /> Delete WG
              </button>
            )}
          </>
        )
      }
    >
      <div className="drawer-stack">
        {mode === 'detail' && wg.isLoading && <p className="subtle">Loading…</p>}

        {/* Tab strip — only in detail mode. Create mode is metadata-only. */}
        {mode === 'detail' && wg.data && (
          <div
            role="tablist"
            aria-label="Workgroup sections"
            style={{
              display: 'flex',
              gap: 4,
              borderBottom: '1px solid var(--line)',
              marginBottom: 12,
            }}
          >
            {(
              [
                ['metadata', `Metadata`],
                ['members', `Members (${members.data?.items.length ?? 0})`],
                ['access', `Access grants`],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                style={{
                  padding: '8px 14px',
                  background: tab === key ? 'rgba(140,21,21,0.08)' : 'transparent',
                  border: 'none',
                  borderBottom: tab === key ? '2px solid #8c1515' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: tab === key ? 700 : 500,
                  color: tab === key ? '#8c1515' : 'inherit',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {(mode === 'create' || tab === 'metadata') && (
          <HelpCallout
            title={mode === 'create' ? 'Creating a workgroup' : 'About this workgroup'}
            whatHappens={
              mode === 'create' ? (
                <>
                  Saving creates a new <code>workgroup</code> row + emits a{' '}
                  <code>workgroup.create</code> audit event. Members added below land as{' '}
                  <code>wg_membership.add</code> events. Reviewer WGs receive approval requests.
                </>
              ) : (
                <>
                  Edits to metadata or membership emit audit events; the new state propagates to the
                  auth gate within a few seconds (cache TTL).
                </>
              )
            }
            youNeed={[
              <>
                A WG ID in the form <code>mcp:&lt;slug&gt;</code> (lowercase, dashes).
              </>,
              <>
                Each member is identified by their full Stanford email (e.g.{' '}
                <code>abhinaya@stanford.edu</code>). For non-Stanford accounts (gmail, etc.), use
                the full email exactly as it appears at sign-in.
              </>,
              <>Bulk imports: paste a CSV with one email per line in the Members panel.</>,
            ]}
            afterwards={[
              <>The WG appears in the registration wizard's owning-WG dropdown.</>,
              <>
                Members signing in see the WG in their effective-WG list at <code>/access</code>.
              </>,
            ]}
          />
        )}

        {/* metadata — always shown in create mode; tab-gated in detail mode */}
        {(mode === 'create' || tab === 'metadata') && (
          <fieldset style={fieldsetStyle} disabled={!editing}>
            <legend style={legendStyle}>Metadata</legend>
            {editing && <RequiredLegend />}
            <Field label="ID (mcp:slug)" required={mode === 'create'}>
              {mode === 'create' ? (
                <input
                  value={formId}
                  onChange={(e) => setFormId(e.target.value)}
                  placeholder="mcp:libraries-platform"
                  style={inputStyle}
                  required
                />
              ) : (
                <code>{formId}</code>
              )}
            </Field>
            <Field label="Display name" required>
              <input
                value={formDisplay}
                onChange={(e) => setFormDisplay(e.target.value)}
                style={inputStyle}
                required
              />
            </Field>
            <Field label="Description">
              <textarea
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </Field>
            <Field label="Owner email">
              <input
                value={formOwner}
                onChange={(e) => setFormOwner(e.target.value)}
                placeholder="abhinaya@stanford.edu"
                style={inputStyle}
              />
            </Field>
            <Field label="Reviewer WG">
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={formReviewer}
                  onChange={(e) => setFormReviewer(e.target.checked)}
                />
                <ShieldCheck size={14} /> Members of this WG can review approvals
              </label>
            </Field>
          </fieldset>
        )}

        {/* members — only in detail mode + when tab is members */}
        {mode === 'detail' && wg.data && tab === 'members' && (
          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>Members ({members.data?.items.length ?? 0})</legend>

            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input
                placeholder="Filter by email…"
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button type="button" onClick={() => setShowCsv((s) => !s)}>
                <Upload size={14} /> CSV
              </button>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <input
                placeholder="add email (e.g. abhinaya@stanford.edu)"
                value={newMember}
                onChange={(e) => setNewMember(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newMember.trim()) onAddMember();
                }}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                className="primary"
                disabled={!newMember.trim() || addMember.isPending}
                onClick={onAddMember}
              >
                <UserPlus size={14} /> Add
              </button>
            </div>

            {showCsv && (
              <div style={{ marginBottom: 12 }}>
                <textarea
                  placeholder={`# one principal_sub per line, optional expiry\nabhinaya\nschouhan,2026-12-31`}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  rows={4}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 13,
                  }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCsv(false);
                      setCsvText('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={onCsvImport}
                    disabled={!csvText.trim() || bulkImport.isPending}
                  >
                    <Upload size={14} /> Import
                  </button>
                </div>
                {csvResult && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: 10,
                      borderRadius: 8,
                      background:
                        csvResult.errors.length === 0
                          ? 'rgba(47, 133, 90, 0.06)'
                          : 'rgba(192, 57, 43, 0.06)',
                      border: '1px solid var(--line)',
                      fontSize: 13,
                    }}
                  >
                    <strong>
                      {csvResult.errors.length === 0
                        ? `✓ Imported ${csvResult.imported} row(s) cleanly`
                        : `Imported ${csvResult.imported} row(s); ${csvResult.errors.length} failed`}
                    </strong>
                    {csvResult.errors.length > 0 && (
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        {csvResult.errors.map((e, i) => (
                          <li key={i} style={{ color: '#7a3030' }}>
                            <code>{JSON.stringify(e.row)}</code>: {e.reason}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                maxHeight: 280,
                overflow: 'auto',
                border: '1px solid var(--line)',
                borderRadius: 8,
              }}
            >
              <table
                style={{
                  fontSize: 13,
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.025)' }}>
                    <th style={memberCellStyle('header')}>Email</th>
                    <th style={memberCellStyle('header')}>Source</th>
                    <th style={memberCellStyle('header')}>Added</th>
                    <th style={{ ...memberCellStyle('header'), width: 80, textAlign: 'right' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ ...memberCellStyle('body'), textAlign: 'center' }}>
                        <span className="subtle">
                          {members.data ? 'No matching members.' : 'Loading…'}
                        </span>
                      </td>
                    </tr>
                  )}
                  {filteredMembers.map((m, idx) => (
                    <tr
                      key={m.principal_sub}
                      style={{
                        background: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.012)',
                      }}
                    >
                      <td style={memberCellStyle('body')}>
                        <code style={{ fontSize: 12 }}>{m.principal_sub}</code>
                      </td>
                      <td style={{ ...memberCellStyle('body'), color: '#6b7280', fontSize: 12 }}>
                        {m.source ?? 'manual'}
                      </td>
                      <td
                        style={{ ...memberCellStyle('body'), color: '#6b7280', fontSize: 12 }}
                        title={m.added_at ? new Date(m.added_at).toLocaleString() : ''}
                      >
                        {m.added_at ? new Date(m.added_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ ...memberCellStyle('body'), textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => onRemoveMember(m.principal_sub)}
                          title="Remove member"
                          style={{
                            padding: '4px 8px',
                            fontSize: 13,
                            border: '1px solid #d97766',
                            color: '#c0392b',
                            background: 'rgba(255,255,255,0.85)',
                            borderRadius: 6,
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </fieldset>
        )}

        {mode === 'detail' && wg.data && tab === 'access' && (
          <AccessGrantsSection wgId={wg.data.id} setActionError={setActionError} />
        )}

        {actionError && (
          <p style={{ color: '#c0392b', fontSize: 14 }} role="alert">
            <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {actionError}
          </p>
        )}
      </div>
    </Drawer>
  );
}

/* ─── Access grants — tools / servers this WG can call ───────────── */

/**
 * WG-centric access management. The matrix on /admin/access is the read
 * view ("who can call X tool"); this section is the write view from the
 * other direction ("which tools is this WG explicitly allowed to call").
 *
 * Data model recap: restrictions live on `mcp_tool.restricted_to_wg_ids`.
 * Empty array = open to anyone with at least one WG. Non-empty = only
 * listed WGs can see / call the tool. So "granting WG access" means
 * adding the WG id to that array; "revoking" means removing it.
 *
 * Two flows:
 *   - Per-tool grant: pick from a searchable dropdown of every tool.
 *   - Per-server grant: pick a server, fans out to every tool of that
 *     server. Most realistic case ("FMS team gets the whole P2P/FMS
 *     server").
 */
function AccessGrantsSection({
  wgId,
  setActionError,
}: {
  wgId: string;
  setActionError: (e: string | null) => void;
}): JSX.Element {
  const tools = useCatalogTools();
  const servers = useCatalogServers();
  const updateTool = useUpdateTool();
  const [toolFilter, setToolFilter] = useState('');
  const [grantToolId, setGrantToolId] = useState('');
  const [grantServerId, setGrantServerId] = useState('');

  const allTools = useMemo(() => {
    return [
      ...(tools.data?.available ?? []),
      ...(tools.data?.requestable ?? []).map((r) => r.tool),
    ];
  }, [tools.data]);

  const serverNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of servers.data?.items ?? []) m.set(s.id, s.name);
    return m;
  }, [servers.data]);

  // Tools where THIS wg is in restricted_to_wg_ids = "granted to this WG explicitly".
  const grantedTools = useMemo(
    () => allTools.filter((t) => ((t.restricted_to_wg_ids ?? []) as string[]).includes(wgId)),
    [allTools, wgId],
  );

  // Tools NOT yet granted to this WG (and not open) — for the picker.
  // Plus tools that are open are also pickable (granting will create
  // the restriction list with this WG).
  const grantableTools = useMemo(
    () =>
      allTools.filter((t) => {
        const r = (t.restricted_to_wg_ids ?? []) as string[];
        return !r.includes(wgId);
      }),
    [allTools, wgId],
  );

  const filteredGrantable = useMemo(() => {
    const q = toolFilter.trim().toLowerCase();
    if (!q) return grantableTools;
    return grantableTools.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q) ||
        (serverNameById.get(t.server_id) ?? '').toLowerCase().includes(q),
    );
  }, [grantableTools, toolFilter, serverNameById]);

  async function grantOne(toolId: string): Promise<void> {
    setActionError(null);
    const t = allTools.find((x) => x.id === toolId);
    if (!t) return;
    const next = [...((t.restricted_to_wg_ids ?? []) as string[])];
    if (!next.includes(wgId)) next.push(wgId);
    try {
      await updateTool.mutateAsync({ id: toolId, patch: { restricted_to_wg_ids: next } });
      setGrantToolId('');
    } catch (e) {
      setActionError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  async function revokeOne(toolId: string): Promise<void> {
    setActionError(null);
    const t = allTools.find((x) => x.id === toolId);
    if (!t) return;
    const next = ((t.restricted_to_wg_ids ?? []) as string[]).filter((id) => id !== wgId);
    try {
      await updateTool.mutateAsync({ id: toolId, patch: { restricted_to_wg_ids: next } });
    } catch (e) {
      setActionError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  async function grantServer(serverId: string): Promise<void> {
    setActionError(null);
    const targets = allTools.filter((t) => t.server_id === serverId);
    try {
      for (const t of targets) {
        const next = [...((t.restricted_to_wg_ids ?? []) as string[])];
        if (!next.includes(wgId)) next.push(wgId);
        await updateTool.mutateAsync({ id: t.id, patch: { restricted_to_wg_ids: next } });
      }
      setGrantServerId('');
    } catch (e) {
      setActionError(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    }
  }

  return (
    <fieldset
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 12,
        marginTop: 8,
      }}
    >
      <legend style={{ fontSize: 13, color: '#786a5a', padding: '0 6px', fontWeight: 600 }}>
        Access grants ({grantedTools.length})
      </legend>
      <p className="subtle" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
        Tools this WG is explicitly granted access to (i.e., this WG appears in the tool's
        <code style={{ marginLeft: 4 }}>restricted_to_wg_ids</code>). For tools that are "open" (no
        restrictions), every WG can call them — they don't need an explicit grant.
      </p>

      {/* Bulk grant per server */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        <select
          value={grantServerId}
          onChange={(e) => setGrantServerId(e.target.value)}
          style={{
            flex: '1 1 200px',
            minWidth: 0,
            padding: '6px 8px',
            border: '1px solid var(--line)',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          <option value="">— grant access to a whole server —</option>
          {(servers.data?.items ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.slug})
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!grantServerId || updateTool.isPending}
          onClick={async () => {
            if (!grantServerId) return;
            const name =
              (servers.data?.items ?? []).find((s) => s.id === grantServerId)?.name ??
              grantServerId;
            if (
              !confirm(
                `Grant ${wgId} access to every tool on "${name}"?\n\n(adds this WG to each tool's restricted_to_wg_ids — also creates the restriction if the tool was previously open)`,
              )
            )
              return;
            await grantServer(grantServerId);
          }}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #5fb27a',
            background: 'rgba(95,178,122,0.15)',
            color: '#1f5a37',
            fontWeight: 600,
            fontSize: 12,
            cursor: grantServerId ? 'pointer' : 'not-allowed',
            opacity: grantServerId ? 1 : 0.6,
          }}
        >
          Grant whole server
        </button>
      </div>

      {/* Per-tool grant picker — searchable */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        <input
          type="search"
          placeholder={`Filter ${grantableTools.length} grantable tools…`}
          value={toolFilter}
          onChange={(e) => setToolFilter(e.target.value)}
          style={{
            flex: '1 1 180px',
            minWidth: 0,
            padding: '6px 8px',
            border: '1px solid var(--line)',
            borderRadius: 6,
            fontSize: 13,
          }}
        />
        <select
          value={grantToolId}
          onChange={(e) => setGrantToolId(e.target.value)}
          style={{
            flex: '2 1 240px',
            minWidth: 0,
            padding: '6px 8px',
            border: '1px solid var(--line)',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          <option value="">— pick a single tool to grant —</option>
          {filteredGrantable.slice(0, 200).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({serverNameById.get(t.server_id) ?? '?'})
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!grantToolId || updateTool.isPending}
          onClick={() => void grantOne(grantToolId)}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #5fb27a',
            background: 'rgba(95,178,122,0.15)',
            color: '#1f5a37',
            fontWeight: 600,
            fontSize: 12,
            cursor: grantToolId ? 'pointer' : 'not-allowed',
            opacity: grantToolId ? 1 : 0.6,
          }}
        >
          Grant tool
        </button>
      </div>

      {/* Granted-tools list */}
      {grantedTools.length === 0 ? (
        <p className="subtle" style={{ fontSize: 12, fontStyle: 'italic', margin: 0 }}>
          No explicit grants. This WG can still call any tool that has no restrictions ("open"
          tools). Use the matrix at <code>/admin/access</code> to see what's open vs restricted.
        </p>
      ) : (
        <table
          style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}
        >
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.025)' }}>
              <th style={memberCellStyle('header')}>Tool</th>
              <th style={memberCellStyle('header')}>Server</th>
              <th style={{ ...memberCellStyle('header'), width: 80, textAlign: 'right' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {grantedTools.map((t, idx) => (
              <tr
                key={t.id}
                style={{ background: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.012)' }}
              >
                <td style={memberCellStyle('body')}>
                  <code style={{ fontSize: 12 }}>{t.name}</code>
                </td>
                <td style={{ ...memberCellStyle('body'), color: '#6b7280', fontSize: 12 }}>
                  {serverNameById.get(t.server_id) ?? '—'}
                </td>
                <td style={{ ...memberCellStyle('body'), textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!confirm(`Revoke ${wgId}'s access to "${t.name}"?`)) return;
                      void revokeOne(t.id);
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: 12,
                      border: '1px solid #d97766',
                      color: '#c0392b',
                      background: 'rgba(255,255,255,0.85)',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                    title="Revoke this WG's access to this tool"
                  >
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </fieldset>
  );
}

function memberCellStyle(kind: 'header' | 'body'): React.CSSProperties {
  return {
    padding: '8px 10px',
    textAlign: 'left',
    borderBottom: '1px solid var(--line)',
    fontWeight: kind === 'header' ? 600 : 400,
    fontSize: kind === 'header' ? 12 : 13,
    color: kind === 'header' ? '#374151' : 'inherit',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  };
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, max-content) 1fr',
        gap: 12,
        alignItems: 'center',
        marginBottom: 8,
      }}
    >
      <span className="subtle" style={{ fontSize: 13 }}>
        {label}
        {required && <Required />}
      </span>
      <span style={{ fontSize: 14 }}>{children}</span>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: 14,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const fieldsetStyle: React.CSSProperties = {
  border: '1px solid var(--line)',
  borderRadius: 8,
  padding: 12,
  marginBottom: 0,
};

const legendStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  padding: '0 6px',
  color: 'var(--muted-foreground, #888)',
};
